import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { useParams } from "react-router-dom";

function PintaPolicy() {
  const { lang } = useParams();

  const texts = {
    en: {
      title: "Pinta Privacy Policy",
      updated: "Last updated: 4th April 2026",
      sections: [
        {
          variant: "h5",
          heading: "1. Introduction",
        },
        {
          variant: "body1",
          content:
            "Pintative Ltd (\"Pinta\", \"we\", \"us\", or \"our\") is committed to protecting your privacy.\n\nThis Privacy Policy explains how we handle your information when you use Pinta – Coloring Book.\n\nOur approach is simple: We collect as little data as possible, and only when strictly necessary.",
        },
        {
          variant: "h5",
          heading: "2. Data We Collect",
        },
        {
          variant: "h6",
          heading: "2.1 Personal Data",
        },
        {
          variant: "body1",
          content:
            "We do not collect personal data unless it is strictly required for specific features.\n\nWe do not:\n• Track users across apps or websites\n• Sell personal data\n• Use advertising identifiers\n• Build user profiles",
        },
        {
          variant: "h6",
          heading: "2.2 User Content",
        },
        {
          variant: "body1",
          content:
            "Content created within the app (such as drawings or preferences):\n• Is stored locally on your device\n• May be stored in your iCloud account, if enabled\n• Is private by default and only shared with others if you choose to do so\n\nYour private content is not accessed or stored on our servers.\n\nIf you choose to share content through community or showcase features, that content will be securely stored only to enable those features.",
        },
        {
          variant: "h6",
          heading: "2.3 Optional Account Features",
        },
        {
          variant: "body1",
          content:
            "Some features (such as community, sharing, or showcase areas) may require account creation.\n\nYou may choose to sign in using:\n• Apple (Sign in with Apple)\n• Google\n\nWe will:\n• Use your credentials only for authentication purposes\n• Not access your external accounts beyond what is required to sign you in\n• Not post or act on your behalf\n• Not sell or share your identity data",
        },
        {
          variant: "h6",
          heading: "2.4 Community & Showcase Features",
        },
        {
          variant: "body1",
          content:
            "Some features of Pinta (such as community galleries or showcase areas) allow you to voluntarily share content with other users.\n\nIf you choose to use these features, you may share:\n• A nickname or display name\n• An avatar or profile image\n• Content you create within the app (such as drawings)\n\nImportant:\n• This information is only visible if you choose to publish it\n• Your content is private by default\n• You remain in control of what you share\n• You can modify or remove your shared content at any time\n\nWe do not make any content public without your explicit action.",
        },
        {
          variant: "h5",
          heading: "3. Third-Party Services",
        },
        {
          variant: "h6",
          heading: "3.1 Apple App Store",
        },
        {
          variant: "body1",
          content:
            "All purchases are handled by Apple through the App Store.\n\nWe do not receive or store your payment details.",
        },
        {
          variant: "h6",
          heading: "3.2 Cloud Services",
        },
        {
          variant: "body1",
          content:
            "We use cloud infrastructure to deliver content (such as templates).\n\nThese services may automatically collect limited technical data, such as:\n• IP address\n• Approximate location\n• Device/network information\n\nThis data is used only for:\n• Performance\n• Security\n• Content delivery optimization\n\nWe do not use this data to identify you personally.",
        },
        {
          variant: "h5",
          heading: "4. Analytics & Tracking",
        },
        {
          variant: "body1",
          content:
            "We do not use third-party analytics tools that track users across apps or services.\n\nIf basic analytics are used in the future, they will be:\n• Privacy-focused\n• Aggregated\n• Non-identifiable",
        },
        {
          variant: "h5",
          heading: "5. Advertising",
        },
        {
          variant: "body1",
          content: "Pinta does not display ads and does not use advertising networks.",
        },
        {
          variant: "h5",
          heading: "6. Data Retention",
        },
        {
          variant: "body1",
          content:
            "• User content remains on your device or iCloud\n• Support emails are deleted once resolved\n• We do not maintain user databases with personal data",
        },
        {
          variant: "h5",
          heading: "7. Your Rights",
        },
        {
          variant: "body1",
          content:
            "Depending on your location (e.g. UK/EU GDPR), you have the right to:\n• Access your data\n• Request deletion\n• Request correction\n\nSince we collect minimal or no personal data, these requests are typically limited in scope.",
        },
        {
          variant: "h5",
          heading: "8. Security",
        },
        {
          variant: "body1",
          content:
            "We take reasonable measures to protect any data involved in providing the service.\n\nHowever, no system is 100% secure.",
        },
        {
          variant: "h5",
          heading: "9. Changes to This Policy",
        },
        {
          variant: "body1",
          content:
            "We may update this Privacy Policy from time to time.\n\nContinued use of the app means you accept the updated version.",
        },
        {
          variant: "h5",
          heading: "10. Contact",
        },
        {
          variant: "body1",
          content:
            "If you have any questions, contact us at:\nsupport@pintative.com",
        },
      ],
    },
    es: {
      title: "Política de Privacidad de Pinta",
      updated: "Última actualización: 4 de abril de 2026",
      sections: [
        { variant: "h5", heading: "1. Introducción" },
        {
          variant: "body1",
          content:
            "Pintative Ltd (\"Pinta\", \"nosotros\", \"nuestro\") se compromete a proteger tu privacidad.\n\nEsta Política de Privacidad explica cómo manejamos tu información cuando usas Pinta – Coloring Book.\n\nNuestro enfoque es simple: recopilamos la menor cantidad de datos posible, y solo cuando es estrictamente necesario.",
        },
        { variant: "h5", heading: "2. Datos que recopilamos" },
        { variant: "h6", heading: "2.1 Datos personales" },
        {
          variant: "body1",
          content:
            "No recopilamos datos personales a menos que sea estrictamente necesario para características específicas.\n\nNo hacemos:\n• Seguimiento de usuarios entre aplicaciones o sitios web\n• Venta de datos personales\n• Uso de identificadores publicitarios\n• Creación de perfiles de usuario",
        },
        { variant: "h6", heading: "2.2 Contenido del usuario" },
        {
          variant: "body1",
          content:
            "El contenido creado dentro de la aplicación (como dibujos o preferencias):\n• Se almacena localmente en tu dispositivo\n• Puede almacenarse en tu cuenta de iCloud, si lo habilitas\n• Es privado de forma predeterminada y solo se comparte con otros si así lo eliges\n\nTu contenido privado no se accede ni se almacena en nuestros servidores.\n\nSi eliges compartir contenido a través de funciones de comunidad o galería, ese contenido se almacenará de forma segura solo para habilitar esas funciones.",
        },
        { variant: "h6", heading: "2.3 Funciones de cuenta opcionales" },
        {
          variant: "body1",
          content:
            "Algunas funciones (como comunidad, compartir o áreas de galería) pueden requerir la creación de una cuenta.\n\nPuedes elegir iniciar sesión usando:\n• Apple (Iniciar sesión con Apple)\n• Google\n\nNosotros:\n• Usaremos tus credenciales solo para propósitos de autenticación\n• No accederemos a tus cuentas externas más allá de lo necesario para iniciar sesión\n• No publicaremos ni actuaremos en tu nombre\n• No venderemos ni compartiremos tus datos de identidad",
        },
        { variant: "h6", heading: "2.4 Funciones de comunidad y galería" },
        {
          variant: "body1",
          content:
            "Algunas funciones de Pinta (como galerías comunitarias o áreas de exhibición) te permiten compartir contenido voluntariamente con otros usuarios.\n\nSi eliges usar estas funciones, puedes compartir:\n• Un apodo o nombre visible\n• Un avatar o imagen de perfil\n• Contenido que crees en la aplicación (como dibujos)\n\nImportante:\n• Esta información solo es visible si decides publicarla\n• Tu contenido es privado por defecto\n• Sigues controlando lo que compartes\n• Puedes modificar o eliminar tu contenido compartido en cualquier momento\n\nNo hacemos público ningún contenido sin tu acción explícita.",
        },
        { variant: "h5", heading: "3. Servicios de terceros" },
        { variant: "h6", heading: "3.1 App Store de Apple" },
        {
          variant: "body1",
          content:
            "Todas las compras se manejan a través de Apple App Store.\n\nNo recibimos ni almacenamos tus detalles de pago.",
        },
        { variant: "h6", heading: "3.2 Servicios en la nube" },
        {
          variant: "body1",
          content:
            "Usamos infraestructura en la nube para entregar contenido (como plantillas).\n\nEstos servicios pueden recopilar automáticamente datos técnicos limitados, como:\n• Dirección IP\n• Ubicación aproximada\n• Información del dispositivo/red\n\nEstos datos se usan solo para:\n• Rendimiento\n• Seguridad\n• Optimización de entrega de contenido\n\nNo usamos estos datos para identificarte personalmente.",
        },
        { variant: "h5", heading: "4. Analítica y seguimiento" },
        {
          variant: "body1",
          content:
            "No usamos herramientas analíticas de terceros que rastreen usuarios entre aplicaciones o servicios.\n\nSi se usan análisis básicos en el futuro, serán:\n• Enfocados en la privacidad\n• Agregados\n• No identificables",
        },
        { variant: "h5", heading: "5. Publicidad" },
        {
          variant: "body1",
          content: "Pinta no muestra anuncios y no usa redes publicitarias.",
        },
        { variant: "h5", heading: "6. Retención de datos" },
        {
          variant: "body1",
          content:
            "• El contenido de los usuarios permanece en tu dispositivo o iCloud\n• Los correos de soporte se eliminan una vez resueltos\n• No mantenemos bases de datos de usuarios con datos personales",
        },
        { variant: "h5", heading: "7. Tus derechos" },
        {
          variant: "body1",
          content:
            "Según tu ubicación (por ejemplo, Reino Unido/UE GDPR), tienes derecho a:\n• Acceder a tus datos\n• Solicitar su eliminación\n• Solicitar su corrección\n\nDado que recopilamos pocos o ningún dato personal, estas solicitudes suelen ser limitadas.",
        },
        { variant: "h5", heading: "8. Seguridad" },
        {
          variant: "body1",
          content:
            "Tomamos medidas razonables para proteger cualquier dato involucrado en la prestación del servicio.\n\nSin embargo, ningún sistema es 100% seguro.",
        },
        { variant: "h5", heading: "9. Cambios en esta política" },
        {
          variant: "body1",
          content:
            "Podemos actualizar esta Política de Privacidad de vez en cuando.\n\nEl uso continuado de la aplicación significa que aceptas la versión actualizada.",
        },
        { variant: "h5", heading: "10. Contacto" },
        {
          variant: "body1",
          content:
            "Si tienes alguna pregunta, contáctanos en:\nsupport@pintative.com",
        },
      ],
    },
    fr: {
      title: "Politique de Confidentialité de Pinta",
      updated: "Dernière mise à jour : 4 avril 2026",
      sections: [
        { variant: "h5", heading: "1. Introduction" },
        {
          variant: "body1",
          content:
            "Pintative Ltd (\"Pinta\", \"nous\", \"notre\") s'engage à protéger votre vie privée.\n\nCette politique de confidentialité explique comment nous traitons vos informations lorsque vous utilisez Pinta – Coloring Book.\n\nNotre approche est simple : nous collectons le moins de données possible, et seulement lorsque c'est strictement nécessaire.",
        },
        { variant: "h5", heading: "2. Données que nous collectons" },
        { variant: "h6", heading: "2.1 Données personnelles" },
        {
          variant: "body1",
          content:
            "Nous ne collectons pas de données personnelles, sauf si cela est strictement nécessaire pour certaines fonctionnalités.\n\nNous ne faisons pas :\n• Suivi des utilisateurs via des applications ou des sites Web\n• Vente de données personnelles\n• Utilisation d'identifiants publicitaires\n• Création de profils d'utilisateurs",
        },
        { variant: "h6", heading: "2.2 Contenu utilisateur" },
        {
          variant: "body1",
          content:
            "Le contenu créé dans l'application (comme des dessins ou des préférences) :\n• Est stocké localement sur votre appareil\n• Peut être stocké sur votre compte iCloud, si activé\n• Est privé par défaut et n'est partagé que si vous choisissez de le faire\n\nVotre contenu privé n'est pas consulté ni stocké sur nos serveurs.\n\nSi vous choisissez de partager du contenu via des fonctionnalités communautaires ou de vitrine, ce contenu sera stocké de manière sécurisée uniquement pour permettre ces fonctionnalités.",
        },
        { variant: "h6", heading: "2.3 Fonctionnalités de compte optionnelles" },
        {
          variant: "body1",
          content:
            "Certaines fonctionnalités (comme la communauté, le partage ou les zones de vitrine) peuvent nécessiter la création d'un compte.\n\nVous pouvez choisir de vous connecter en utilisant :\n• Apple (Se connecter avec Apple)\n• Google\n\nNous :\n• Utiliserons vos informations d'identification uniquement à des fins d'authentification\n• N'accéderons pas à vos comptes externes au-delà de ce qui est nécessaire pour vous connecter\n• Ne publierons pas ni n'agirons pas en votre nom\n• Ne vendrons ni ne partagerons vos données d'identité",
        },
        { variant: "h6", heading: "2.4 Fonctionnalités communautaires et vitrine" },
        {
          variant: "body1",
          content:
            "Certaines fonctions de Pinta (comme les galeries communautaires ou les zones de vitrine) vous permettent de partager volontairement du contenu avec d'autres utilisateurs.\n\nSi vous choisissez d'utiliser ces fonctions, vous pouvez partager :\n• Un pseudo ou un nom d'affichage\n• Un avatar ou une image de profil\n• Du contenu que vous créez dans l'application (comme des dessins)\n\nImportant :\n• Ces informations ne sont visibles que si vous choisissez de les publier\n• Votre contenu est privé par défaut\n• Vous gardez le contrôle de ce que vous partagez\n• Vous pouvez modifier ou supprimer votre contenu partagé à tout moment\n\nNous ne rendons aucun contenu public sans votre action explicite.",
        },
        { variant: "h5", heading: "3. Services tiers" },
        { variant: "h6", heading: "3.1 App Store d'Apple" },
        {
          variant: "body1",
          content:
            "Tous les achats sont traités par Apple via l'App Store.\n\nNous ne recevons ni ne stockons vos informations de paiement.",
        },
        { variant: "h6", heading: "3.2 Services Cloud" },
        {
          variant: "body1",
          content:
            "Nous utilisons une infrastructure cloud pour fournir du contenu (comme des modèles).\n\nCes services peuvent automatiquement collecter des données techniques limitées, telles que :\n• Adresse IP\n• Emplacement approximatif\n• Informations sur l'appareil/le réseau\n\nCes données sont utilisées uniquement pour :\n• Performance\n• Sécurité\n• Optimisation de la diffusion de contenu\n\nNous n'utilisons pas ces données pour vous identifier personnellement.",
        },
        { variant: "h5", heading: "4. Analyses et suivi" },
        {
          variant: "body1",
          content:
            "Nous n'utilisons pas d'outils d'analyse tiers qui suivent les utilisateurs entre des applications ou des services.\n\nSi des analyses de base sont utilisées à l'avenir, elles seront :\n• Axées sur la confidentialité\n• Agrégées\n• Non identifiables",
        },
        { variant: "h5", heading: "5. Publicité" },
        {
          variant: "body1",
          content: "Pinta n'affiche pas de publicités et n'utilise pas de réseaux publicitaires.",
        },
        { variant: "h5", heading: "6. Conservation des données" },
        {
          variant: "body1",
          content:
            "• Le contenu utilisateur reste sur votre appareil ou iCloud\n• Les emails de support sont supprimés une fois résolus\n• Nous ne maintenons pas de bases de données utilisateur avec des données personnelles",
        },
        { variant: "h5", heading: "7. Vos droits" },
        {
          variant: "body1",
          content:
            "Selon votre emplacement (par exemple, Royaume-Uni/UE GDPR), vous avez le droit de :\n• Accéder à vos données\n• Demander leur suppression\n• Demander leur correction\n\nÉtant donné que nous collectons peu ou pas de données personnelles, ces demandes sont généralement limitées.",
        },
        { variant: "h5", heading: "8. Sécurité" },
        {
          variant: "body1",
          content:
            "Nous prenons des mesures raisonnables pour protéger les données impliquées dans la fourniture du service.\n\nCependant, aucun système n'est 100 % sécurisé.",
        },
        { variant: "h5", heading: "9. Modifications de cette politique" },
        {
          variant: "body1",
          content:
            "Nous pouvons mettre à jour cette politique de confidentialité de temps à autre.\n\nL'utilisation continue de l'application signifie que vous acceptez la version mise à jour.",
        },
        { variant: "h5", heading: "10. Contact" },
        {
          variant: "body1",
          content:
            "Si vous avez des questions, contactez-nous à :\nsupport@pintative.com",
        },
      ],
    },
    de: {
      title: "Datenschutzrichtlinie von Pinta",
      updated: "Zuletzt aktualisiert: 4. April 2026",
      sections: [
        { variant: "h5", heading: "1. Einführung" },
        {
          variant: "body1",
          content:
            "Pintative Ltd (\"Pinta\", \"wir\", \"uns\") verpflichtet sich, Ihre Privatsphäre zu schützen.\n\nDiese Datenschutzrichtlinie erklärt, wie wir Ihre Informationen verarbeiten, wenn Sie Pinta – Coloring Book verwenden.\n\nUnser Ansatz ist einfach: Wir erfassen so wenige Daten wie möglich und nur dann, wenn dies unbedingt erforderlich ist.",
        },
        { variant: "h5", heading: "2. Daten, die wir erfassen" },
        { variant: "h6", heading: "2.1 Persönliche Daten" },
        {
          variant: "body1",
          content:
            "Wir erfassen keine persönlichen Daten, es sei denn, dies ist für bestimmte Funktionen unbedingt erforderlich.\n\nWir tun nicht:\n• Benutzer über Apps oder Websites hinweg verfolgen\n• Persönliche Daten verkaufen\n• Werbekennungen verwenden\n• Benutzerprofile erstellen",
        },
        { variant: "h6", heading: "2.2 Benutzerinhalte" },
        {
          variant: "body1",
          content:
            "Inhalte, die in der App erstellt werden (wie Zeichnungen oder Einstellungen):\n• Werden lokal auf Ihrem Gerät gespeichert\n• Können in Ihrem iCloud-Konto gespeichert werden, wenn aktiviert\n• Sind standardmäßig privat und werden nur geteilt, wenn Sie dies wählen\n\nIhre privaten Inhalte werden nicht auf unseren Servern abgerufen oder gespeichert.\n\nWenn Sie Inhalte über Community- oder Showcase-Funktionen teilen, werden diese Inhalte sicher gespeichert, nur um diese Funktionen zu ermöglichen.",
        },
        { variant: "h6", heading: "2.3 Optionale Kontofunktionen" },
        {
          variant: "body1",
          content:
            "Einige Funktionen (wie Community, Teilen oder Showcase-Bereiche) können die Erstellung eines Kontos erfordern.\n\nSie können sich mit folgendem anmelden :\n• Apple (Mit Apple anmelden)\n• Google\n\nWir werden:\n• Ihre Zugangsdaten nur für Authentifizierungszwecke verwenden\n• Nicht auf Ihre externen Konten zugreifen, außer wenn dies zum Anmelden erforderlich ist\n• Nicht in Ihrem Namen posten oder handeln\n• Ihre Identitätsdaten nicht verkaufen oder weitergeben",
        },
        { variant: "h6", heading: "2.4 Community- und Showcase-Funktionen" },
        {
          variant: "body1",
          content:
            "Einige Funktionen von Pinta (wie Community-Galerien oder Showcase-Bereiche) ermöglichen es Ihnen, freiwillig Inhalte mit anderen Nutzern zu teilen.\n\nWenn Sie diese Funktionen nutzen, können Sie teilen:\n• Einen Spitznamen oder Anzeigenamen\n• Ein Avatar- oder Profilbild\n• Inhalte, die Sie in der App erstellen (wie Zeichnungen)\n\nWichtig:\n• Diese Informationen sind nur sichtbar, wenn Sie sie veröffentlichen\n• Ihre Inhalte sind standardmäßig privat\n• Sie behalten die Kontrolle darüber, was Sie teilen\n• Sie können Ihre geteilten Inhalte jederzeit ändern oder entfernen\n\nWir machen keine Inhalte öffentlich, ohne dass Sie dies ausdrücklich wünschen.",
        },
        { variant: "h5", heading: "3. Drittanbieterdienste" },
        { variant: "h6", heading: "3.1 Apple App Store" },
        {
          variant: "body1",
          content:
            "Alle Käufe werden von Apple über den App Store abgewickelt.\n\nWir erhalten oder speichern Ihre Zahlungsdaten nicht.",
        },
        { variant: "h6", heading: "3.2 Cloud-Dienste" },
        {
          variant: "body1",
          content:
            "Wir nutzen Cloud-Infrastruktur, um Inhalte bereitzustellen (wie Vorlagen).\n\nDiese Dienste können automatisch begrenzte technische Daten erfassen, wie:\n• IP-Adresse\n• Ungefähren Standort\n• Geräte-/Netzwerkdaten\n\nDiese Daten werden nur verwendet für:\n• Leistung\n• Sicherheit\n• Optimierung der Inhaltsbereitstellung\n\nWir verwenden diese Daten nicht, um Sie persönlich zu identifizieren.",
        },
        { variant: "h5", heading: "4. Analytik & Tracking" },
        {
          variant: "body1",
          content:
            "Wir verwenden keine Drittanbieter-Analysetools, die Nutzer über Apps oder Dienste hinweg verfolgen.\n\nWenn in Zukunft einfache Analysen verwendet werden, werden sie:\n• Datenschutzorientiert sein\n• Aggregiert sein\n• Nicht identifizierbar sein",
        },
        { variant: "h5", heading: "5. Werbung" },
        {
          variant: "body1",
          content: "Pinta zeigt keine Werbung an und verwendet keine Werbenetzwerke.",
        },
        { variant: "h5", heading: "6. Datenaufbewahrung" },
        {
          variant: "body1",
          content:
            "• Benutzerinhalte verbleiben auf Ihrem Gerät oder in iCloud\n• Support-E-Mails werden nach Abschluss gelöscht\n• Wir führen keine Benutzerdatenbanken mit personenbezogenen Daten",
        },
        { variant: "h5", heading: "7. Ihre Rechte" },
        {
          variant: "body1",
          content:
            "Je nach Ihrem Standort (z. B. UK/EU GDPR) haben Sie das Recht:\n• Auf Ihre Daten zuzugreifen\n• Ihre Löschung zu verlangen\n• Ihre Berichtigung zu verlangen\n\nDa wir nur wenige oder keine personenbezogenen Daten erfassen, sind diese Anfragen in der Regel eingeschränkt.",
        },
        { variant: "h5", heading: "8. Sicherheit" },
        {
          variant: "body1",
          content:
            "Wir ergreifen angemessene Maßnahmen, um alle Daten zu schützen, die an der Bereitstellung des Dienstes beteiligt sind.\n\nJedoch ist kein System zu 100 % sicher.",
        },
        { variant: "h5", heading: "9. Änderungen dieser Richtlinie" },
        {
          variant: "body1",
          content:
            "Wir können diese Datenschutzrichtlinie von Zeit zu Zeit aktualisieren.\n\nDie fortgesetzte Nutzung der App bedeutet, dass Sie die aktualisierte Version akzeptieren.",
        },
        { variant: "h5", heading: "10. Kontakt" },
        {
          variant: "body1",
          content:
            "Bei Fragen kontaktieren Sie uns unter:\nsupport@pintative.com",
        },
      ],
    },
    it: {
      title: "Informativa sulla Privacy di Pinta",
      updated: "Ultimo aggiornamento: 4 aprile 2026",
      sections: [
        { variant: "h5", heading: "1. Introduzione" },
        {
          variant: "body1",
          content:
            "Pintative Ltd (\"Pinta\", \"noi\", \"nostro\") si impegna a proteggere la tua privacy.\n\nQuesta informativa sulla privacy spiega come trattiamo le tue informazioni quando usi Pinta – Coloring Book.\n\nIl nostro approccio è semplice: raccogliamo il minor numero di dati possibile e solo quando è strettamente necessario.",
        },
        { variant: "h5", heading: "2. Dati che raccogliamo" },
        { variant: "h6", heading: "2.1 Dati personali" },
        {
          variant: "body1",
          content:
            "Non raccogliamo dati personali a meno che non sia strettamente necessario per funzionalità specifiche.\n\nNon facciamo:\n• Tracciamento degli utenti tra app o siti web\n• Vendita di dati personali\n• Utilizzo di identificatori pubblicitari\n• Creazione di profili utente",
        },
        { variant: "h6", heading: "2.2 Contenuto dell'utente" },
        {
          variant: "body1",
          content:
            "I contenuti creati all'interno dell'app (come disegni o preferenze):\n• Vengono archiviati localmente sul tuo dispositivo\n• Possono essere archiviati nel tuo account iCloud, se abilitato\n• Sono privati per impostazione predefinita e condivisi solo se decidi di farlo\n\nI tuoi contenuti privati non vengono consultati né archiviati sui nostri server.\n\nSe scegli di condividere contenuti tramite funzionalità community o showcase, tali contenuti verranno archiviati in modo sicuro solo per consentire tali funzionalità.",
        },
        { variant: "h6", heading: "2.3 Funzionalità account facoltative" },
        {
          variant: "body1",
          content:
            "Alcune funzionalità (come community, condivisione o aree showcase) possono richiedere la creazione di un account.\n\nPuoi scegliere di accedere utilizzando :\n• Apple (Accedi con Apple)\n• Google\n\nNoi:\n• Utilizzeremo le tue credenziali solo per scopi di autenticazione\n• Non accederemo ai tuoi account esterni oltre quanto necessario per effettuare l'accesso\n• Non pubblicheremo né agiremo per tuo conto\n• Non venderemo né condivideremo i tuoi dati di identità",
        },
        { variant: "h6", heading: "2.4 Funzionalità community e showcase" },
        {
          variant: "body1",
          content:
            "Alcune funzionalità di Pinta (come gallerie community o aree showcase) ti consentono di condividere volontariamente contenuti con altri utenti.\n\nSe scegli di usare queste funzionalità, puoi condividere:\n• Un soprannome o nome visualizzato\n• Un avatar o immagine del profilo\n• Contenuti che crei nell'app (come disegni)\n\nImportante:\n• Queste informazioni sono visibili solo se decidi di pubblicarle\n• I tuoi contenuti sono privati per impostazione predefinita\n• Rimani tu a decidere cosa condividere\n• Puoi modificare o rimuovere i tuoi contenuti condivisi in qualsiasi momento\n\nNon rendiamo pubblici i contenuti senza il tuo esplicito consenso.",
        },
        { variant: "h5", heading: "3. Servizi di terze parti" },
        { variant: "h6", heading: "3.1 App Store di Apple" },
        {
          variant: "body1",
          content:
            "Tutti gli acquisti sono gestiti da Apple tramite l'App Store.\n\nNon riceviamo né memorizziamo i tuoi dati di pagamento.",
        },
        { variant: "h6", heading: "3.2 Servizi cloud" },
        {
          variant: "body1",
          content:
            "Utilizziamo infrastruttura cloud per fornire contenuti (come modelli).\n\nQuesti servizi possono raccogliere automaticamente dati tecnici limitati, come:\n• Indirizzo IP\n• Posizione approssimativa\n• Informazioni sul dispositivo/rete\n\nQuesti dati sono usati solo per:\n• Prestazioni\n• Sicurezza\n• Ottimizzazione della consegna dei contenuti\n\nNon utilizziamo questi dati per identificarti personalmente.",
        },
        { variant: "h5", heading: "4. Analisi e tracciamento" },
        {
          variant: "body1",
          content:
            "Non utilizziamo strumenti di analisi di terze parti che tracciano gli utenti tra app o servizi.\n\nSe in futuro verranno utilizzate analisi di base, saranno:\n• Incentrate sulla privacy\n• Aggregati\n• Non identificabili",
        },
        { variant: "h5", heading: "5. Pubblicità" },
        {
          variant: "body1",
          content: "Pinta non mostra annunci e non utilizza reti pubblicitarie.",
        },
        { variant: "h5", heading: "6. Conservazione dei dati" },
        {
          variant: "body1",
          content:
            "• I contenuti dell'utente rimangono sul tuo dispositivo o su iCloud\n• Le email di supporto vengono eliminate una volta risolte\n• Non manteniamo database utenti con dati personali",
        },
        { variant: "h5", heading: "7. I tuoi diritti" },
        {
          variant: "body1",
          content:
            "A seconda della tua posizione (ad esempio, Regno Unito/UE GDPR), hai il diritto di:\n• Accedere ai tuoi dati\n• Richiedere la loro cancellazione\n• Richiedere la loro correzione\n\nPoiché raccogliamo pochi o nessun dato personale, queste richieste sono generalmente limitate.",
        },
        { variant: "h5", heading: "8. Sicurezza" },
        {
          variant: "body1",
          content:
            "Adottiamo misure ragionevoli per proteggere i dati coinvolti nella fornitura del servizio.\n\nTuttavia, nessun sistema è al 100% sicuro.",
        },
        { variant: "h5", heading: "9. Modifiche a questa politica" },
        {
          variant: "body1",
          content:
            "Potremmo aggiornare questa informativa sulla privacy di tanto in tanto.\n\nL'uso continuato dell'app significa che accetti la versione aggiornata.",
        },
        { variant: "h5", heading: "10. Contatto" },
        {
          variant: "body1",
          content:
            "Se hai domande, contattaci a:\nsupport@pintative.com",
        },
      ],
    },
  };

  const t = texts[lang] || texts.en;

  return (
    <Box sx={{ maxWidth: "800px", mx: "auto", p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t.updated}
        </Typography>

        {t.sections.map((section, index) => (
          <Box key={index} sx={{ mt: section.variant.startsWith("h") ? 3 : 0 }}>
            {section.heading && (
              <Typography
                variant={section.variant}
                sx={{ fontWeight: "bold", mb: section.content ? 1 : 2 }}
              >
                {section.heading}
              </Typography>
            )}
            {section.content && (
              <Typography variant="body1" sx={{ whiteSpace: "pre-line", mb: 2 }}>
                {section.content}
              </Typography>
            )}
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

export default PintaPolicy;
